import { PlaybookCardSection } from "../PlaybookCardSection";
import { useTranslate } from "#src/hooks";
import { Col, Collapse, Form, Typography } from "antd";
import { CaretUpIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";
import styled from "styled-components";
import { MediaGenerationModeFormItem } from "../MediaGenerationModeFormItem";
import { FormItemCol } from "../FormItemCol";
import {
  buildBooleanOptions,
  buildChatModeOptions,
  buildStoryboardGenerationModeOptions,
} from "../../config/fieldsConfig";
import { BooleanOption } from "../../types";

// Semantic keys for the advanced Collapse panels — stable identifiers that
// survive panel reordering, unlike positional numbers. Exported so callers can
// target a specific panel (e.g. auto-expand "images" when a save is blocked).
export const ADVANCED_PANEL_KEYS = {
  general: "general",
  images: "images",
  audio: "audio",
  chat: "chat",
  technical: "technical",
} as const;

export type AdvancedSectionProps = {
  isAdmin?: boolean;
  activeKeys: string[];
  onActiveKeysChange: (keys: string[]) => void;
};

export const AdvancedSection = ({
  isAdmin = false,
  activeKeys,
  onActiveKeysChange,
}: AdvancedSectionProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  // Read from the surrounding <Form> context (no explicit form instance needed).
  // A music asset is only meaningful while auto-select is "enabled".
  const autoSelectMusic = Form.useWatch<BooleanOption>("auto_select_music");
  const isMusicEnabled = autoSelectMusic === "enabled";
  const items = [
    {
      key: ADVANCED_PANEL_KEYS.general,
      label: (
        <Typography.Text style={{ fontWeight: 500 }}>
          {t("pages.playbooks.sections.advanced.options.general").toUpperCase()}
        </Typography.Text>
      ),
      children: (
        <>
          <Col xs={24}>
            <MediaGenerationModeFormItem />
          </Col>
          <FormItemCol
            name="transitions"
            label={t("pages.playbooks.sections.advanced.defaultTransitions")}
            component="radio"
            options={buildBooleanOptions(t)}
            helpText={t(
              "pages.playbooks.sections.advanced.defaultTransitionsHelp"
            )}
          />
        </>
      ),
    },
    {
      key: ADVANCED_PANEL_KEYS.images,
      label: (
        <Typography.Text style={{ fontWeight: 500 }}>
          {t("pages.playbooks.sections.advanced.options.images").toUpperCase()}
        </Typography.Text>
      ),
      children: (
        <>
          <FormItemCol
            name="image_style_description"
            label={t("pages.playbooks.sections.advanced.imageStyleDescription")}
            component="textarea"
            placeholder={t(
              "pages.playbooks.sections.advanced.imageStyleDescriptionPlaceholder"
            )}
            rows={3}
          />
          <FormItemCol
            name="include_document_animations"
            component="toggle"
            toggleLabel={t(
              "pages.playbooks.sections.advanced.includeDocumentAnimations"
            )}
            helpText={t(
              "pages.playbooks.sections.advanced.includeDocumentAnimationsHelp"
            )}
          />
          <FormItemCol
            name="generate_images_for_slides"
            toggleLabel={t(
              "pages.playbooks.sections.advanced.generateImagesForSlides"
            )}
            component="toggle"
          />
          <FormItemCol
            name="image_extraction_enabled"
            toggleLabel={t(
              "pages.playbooks.sections.advanced.imageExtractionEnabled"
            )}
            component="toggle"
            helpText={t(
              "pages.playbooks.sections.advanced.imageExtractionEnabledHelp"
            )}
          />
          <FormItemCol
            name="image_sourcing_enabled"
            toggleLabel={t(
              "pages.playbooks.sections.advanced.imageSourcingEnabled"
            )}
            component="toggle"
            helpText={t(
              "pages.playbooks.sections.advanced.imageSourcingEnabledHelp"
            )}
          />
          <FormItemCol
            name="video_sourcing_enabled"
            toggleLabel={t(
              "pages.playbooks.sections.advanced.videoSourcingEnabled"
            )}
            component="toggle"
            helpText={t(
              "pages.playbooks.sections.advanced.videoSourcingEnabledHelp"
            )}
          />
        </>
      ),
    },
    {
      key: ADVANCED_PANEL_KEYS.audio,
      label: (
        <Typography.Text style={{ fontWeight: 500 }}>
          {t("pages.playbooks.sections.advanced.options.audio").toUpperCase()}
        </Typography.Text>
      ),
      children: (
        <FormItemCol
          name="generate_emotions"
          toggleLabel={t("pages.playbooks.sections.advanced.generateEmotions")}
          component="toggle"
          helpText={t("pages.playbooks.sections.advanced.generateEmotionsHelp")}
        />
      ),
    },
    {
      key: ADVANCED_PANEL_KEYS.chat,
      label: (
        <Typography.Text style={{ fontWeight: 500 }}>
          {t("pages.playbooks.sections.advanced.options.chat").toUpperCase()}
        </Typography.Text>
      ),
      children: (
        <FormItemCol
          name="chat_mode"
          component="radio"
          options={buildChatModeOptions(t)}
          placeholder={t("pages.playbooks.sections.advanced.selectChatMode")}
        />
      ),
    },
    ...(isAdmin
      ? [
          {
            key: ADVANCED_PANEL_KEYS.technical,
            label: (
              <Typography.Text style={{ fontWeight: 500 }}>
                {t(
                  "pages.playbooks.sections.advanced.options.technical"
                ).toUpperCase()}
              </Typography.Text>
            ),
            children: (
              <>
                <FormItemCol
                  name="script_template_id"
                  label={t("pages.playbooks.sections.advanced.scriptTemplate")}
                  component="input"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.scriptTemplatePlaceholder"
                  )}
                />
                <FormItemCol
                  name="highlights_template_id"
                  label={t(
                    "pages.playbooks.sections.advanced.highlightsTemplate"
                  )}
                  component="input"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.highlightsTemplatePlaceholder"
                  )}
                />
                <FormItemCol
                  name="cta_group_id"
                  label={t("pages.playbooks.sections.advanced.ctaGroupId")}
                  component="input"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.ctaGroupIdPlaceholder"
                  )}
                />
                <FormItemCol
                  name="auto_select_music"
                  label={t("pages.playbooks.sections.advanced.autoSelectMusic")}
                  component="radio"
                  options={buildBooleanOptions(t)}
                  helpText={t(
                    "pages.playbooks.sections.advanced.autoSelectMusicHelp"
                  )}
                />
                {/* Music asset is required (and only relevant) when auto-select
                    is "enabled"; hidden otherwise and cleared on save. */}
                {isMusicEnabled && (
                  <FormItemCol
                    name="music_asset_id"
                    label={t("pages.playbooks.sections.advanced.musicAssetId")}
                    component="input"
                    placeholder={t(
                      "pages.playbooks.sections.advanced.musicAssetIdPlaceholder"
                    )}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "pages.playbooks.sections.advanced.musicAssetIdRequired"
                        ),
                      },
                    ]}
                  />
                )}
                <FormItemCol
                  name="instance_slug"
                  label={t("pages.playbooks.sections.advanced.instanceSlug")}
                  component="input"
                  prefix="/"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.instanceSlugPlaceholder"
                  )}
                />
                <FormItemCol
                  name="selected_pages"
                  label={t("pages.playbooks.sections.advanced.selectedPages")}
                  component="input"
                  placeholder={t(
                    "projects.components.ProjectSettingForm.example12510Numbers"
                  )}
                />
                <FormItemCol
                  name="storyboard_generation_mode"
                  label={t(
                    "pages.playbooks.sections.advanced.storyboardGenerationMode"
                  )}
                  component="radio"
                  options={buildStoryboardGenerationModeOptions(t)}
                  placeholder={t(
                    "pages.playbooks.sections.advanced.selectStoryboardGenerationMode"
                  )}
                />
                <FormItemCol
                  name="generation_date"
                  label={t("pages.playbooks.sections.advanced.generationDate")}
                  component="date"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.generationDatePlaceholder"
                  )}
                />
                <FormItemCol
                  name="expiration_time"
                  label={t("pages.playbooks.sections.advanced.expirationTime")}
                  component="date"
                  placeholder={t(
                    "pages.playbooks.sections.advanced.expirationTimePlaceholder"
                  )}
                />
                <FormItemCol
                  name="preserve_original_doc"
                  component="toggle"
                  toggleLabel={t(
                    "pages.playbooks.sections.advanced.preserveOriginalDoc"
                  )}
                />
              </>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <PlaybookCardSection
        title={t("pages.playbooks.sections.advanced.advancedSettings")}
        style={{
          marginBottom: 0,
        }}
      />
      <StyledCollapse
        size="large"
        bordered={false}
        activeKey={activeKeys}
        onChange={(keys) =>
          onActiveKeysChange(Array.isArray(keys) ? keys : [keys])
        }
        expandIconPosition="end"
        expandIcon={(panelProps) => {
          const rotateDeg = panelProps.isActive ? 0 : 180;
          return (
            <CaretUpIcon
              size={12}
              style={{
                transform: `rotate(${rotateDeg}deg)`,
                transition: "transform 0.2s ease-in-out",
                color: theme.colorTextBtnDisabled,
              }}
            />
          );
        }}
        items={items}
      />
    </>
  );
};

const StyledCollapse = styled(Collapse)`
  /* to avoid card wrapper padding for collapse */
  margin-left: -16px;
  margin-right: -16px;

  .ant-collapse-content {
    background-color: ${({ theme }) => theme.colorBgContainer} !important;
    .ant-collapse-content-box {
      padding: 16px !important;
    }
  }
`;
